import dayjs from 'dayjs';
import useFetchData from 'hooks/useFetchData';
import { useMemo, useRef } from 'react';
import { sortDateAndTime } from 'utils/sorters';
import useZoomByMouseWheel from '../Hooks/useZoomByMouseWheel';
import { createForegroundPositionExpression, createHandleFgImgMouseMove, dateFormatIG } from '../IGconfig';
import CompetitionLogo from '../Parts/CompetitionLogo';
import GameScoreCard from '../Parts/GameScoreCard';
import IGHeader from '../Parts/IGHeader';

const IG2DayRecapCore = (props) => {
  const {
    exportRef,
    metadata,
    size,
    fgImgPositionX,
    fgImgPositionY,
    setFgImgPositionX,
    setFgImgPositionY,
    fgImgZoom,
    setFgImgZoom,
    fgPictureUrl,
    fgImgMargin,
    backgroundMode,
    backgroundImgUrl,
    backgroundColor,
    seasonId,
    phaseIdsAll,
    venueIdsAll,
    enablePositioning = true,
    useImageProxy = true
  } = props || {};
  const safeMetadata = metadata ?? {};
  const fgImgRef = useRef(null);
  const effectiveMode = backgroundMode ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  const todayStr = dayjs().format(dateFormatIG);
  const rawDate = safeMetadata.Date;
  const effectiveDate = (rawDate != null && String(rawDate).trim() !== '') ? String(rawDate).trim() : todayStr;
  const fetchUrl = seasonId != null && effectiveDate ? `/public/games?seasonId=${seasonId}&startDate=${effectiveDate}` : null;

  // Always call hooks, but conditionally use positioning functionality
  useZoomByMouseWheel(fgImgRef, enablePositioning ? fgImgZoom : null, enablePositioning ? setFgImgZoom : () => {});
  const handleFgImgMouseMove = enablePositioning 
    ? createHandleFgImgMouseMove(setFgImgPositionX, setFgImgPositionY)
    : null;

  // Load games data using public API
  const { data: gamesData } = useFetchData(fetchUrl);

  // Filter games data to only include games that are on effectiveDate and with phaseId in phaseIdsAll and venueId in venueIdsAll
  const gamesToday = gamesData?.filter((game) => {
    return game.startDate === effectiveDate &&
           (phaseIdsAll?.includes(game.Phase?.phaseId) ?? true) &&
           (venueIdsAll?.includes(game.Venue?.venueId) ?? true);
  });

  const data = useMemo(() => {
    return gamesToday
      ?.sort((g1, g2) => sortDateAndTime(g1.startDate, g1.startTime, g2.startDate, g2.startTime))
      .map((game) => ({
        VenueName: game.Venue?.name,
        VenueLogo: game.Venue?.logoUrl?.replace("[size]", "cropped_md"),
        HomeTeamLogo: game.HomeTeam?.logoUrl?.replace("[size]", "cropped_md"),
        AwayTeamLogo: game.AwayTeam?.logoUrl?.replace("[size]", "cropped_md"),
        Score: `${game.HomeTeamGoals}:${game.AwayTeamGoals}`,
        GroupName: game.Phase?.Group?.name,
        GroupColor: game.Phase?.Group?.color,
      })) ?? [];
  }, [gamesToday]);

  return (
    <article
      ref={exportRef}
      className="mx-auto h-[1000px] w-[1100px] bg-hockey-page-bg font-frontline uppercase text-white"
      style={{
        width: `${size?.width}px`,
        height: `${size?.height}px`,
        ...(effectiveMode === 'COLOR' && backgroundColor ? { backgroundColor } : {}),
      }}
    >
      <main 
        className="grid h-full grid-cols-5" 
        style={{ 
          ...(effectiveMode === 'COLOR' && backgroundColor
            ? { backgroundColor }
            : effectiveMode === 'IMAGE' && backgroundImgUrl
              ? {
                  backgroundImage: `url('${useImageProxy ? `${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}` : backgroundImgUrl}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }
              : {
                  backgroundImage: `url('/images/BG2025_dark_portrait.png')`,
                  backgroundSize: 'cover',
                }),
        }}
      >
        <div 
          className="col-span-3 flex flex-col p-8 pr-0" 
          style={{
            paddingLeft: `${fgImgMargin.left}px`,
            paddingTop: `${fgImgMargin.top}px`,
            paddingRight: `${fgImgMargin.right}px`,
            paddingBottom: `${fgImgMargin.bottom}px`,
          }}
        >
          <IGHeader
            className="border-b-2 border-white"
            title={`${safeMetadata.CompetitionName ?? ''} daily recap`}
            date={effectiveDate}
          />

          <div className="flex h-full flex-col items-center justify-between">
            <div className="flex flex-col gap-2">
              {data.map((game, index) => (
                <GameScoreCard key={index} game={game} useImageProxy={useImageProxy} />
              ))}
            </div>
            <CompetitionLogo
              src={safeMetadata.CompetitionLogo}
              title={safeMetadata.CompetitionName}
              season={safeMetadata.SeasonName}
              useImageProxy={useImageProxy}
            />
          </div>
        </div>
        <div
          ref={fgImgRef}
          onMouseMove={handleFgImgMouseMove}
          className='w-full relative col-span-2 h-full'
          style={{
            backgroundImage: `url('${useImageProxy ? `${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${fgPictureUrl}` : fgPictureUrl}')`,
            backgroundSize: `${fgImgZoom ?? 100}%`,
            backgroundPosition: createForegroundPositionExpression(fgImgPositionX, fgImgPositionY),
            backgroundRepeat: 'no-repeat',
            cursor: enablePositioning ? 'grab' : 'default',
            clipPath: "polygon(50px 0, 100% 0, 100% 100%, 0% 100%)",
          }}
          onMouseDown={enablePositioning ? (e) => e.target.style.cursor = 'grabbing' : undefined}
          onMouseUp={enablePositioning ? (e) => e.target.style.cursor = 'grab' : undefined}
        />
      </main>
    </article>
  );
};

export default IG2DayRecapCore;
